import React, { Suspense } from "react";
import { ContainerMixin, ContainerMixinPayload, ContainerPayload, SerializedContainer } from "../mixins/ContainerMixin";
import { EmbedMixin, EmbedMixinPayload, EmbedPayload, SerializedEmbed } from "../mixins/EmbedMixin";
import { $applyNodeReplacement, DecoratorNode, SerializedLexicalNode, Spread } from "lexical";
import { InlineMixin } from "../mixins/InlineMixin";

const InlineComponent = React.lazy(() => import('../mixins/components/InlineComponent'));

export interface InlineYoutubePayload extends ContainerPayload, EmbedPayload {
  key?: string;
}

export type SerializedInlineYoutubeNode = Spread<
  SerializedContainer & SerializedEmbed,
  SerializedLexicalNode
>;

class InlineYoutubeBase extends DecoratorNode<JSX.Element> implements ContainerMixinPayload, EmbedMixinPayload {
  __embedSrc!: string;
  __width!: number;
  __height!: number;

  constructor(
    embedSrc: string,
    width?: number,
    height?: number,
    key?: string,
  ) {
    super(key);
    this.__width = width || 560;
    this.__height = height || 315;
    this.__embedSrc = embedSrc;
  }

  createDOM(_config: unknown, _editor: unknown): HTMLElement {
    const element = document.createElement('span');
    element.className = 'inline-youtube';
    return element;
  }

  updateDOM(_prevNode: unknown, _dom: HTMLElement, _config: unknown): boolean {
    return false;
  }

  exportJSON(): SerializedInlineYoutubeNode {
    const self = this.getLatest();
    return {
      type: InlineYoutubeNode.getType(),
      version: 1,
      embedSrc: self.__embedSrc,
      width: self.__width,
      height: self.__height,
    };
  }
}

export class InlineYoutubeNode extends EmbedMixin(InlineMixin(ContainerMixin(InlineYoutubeBase))) {
  static getType() {
    return 'inline-youtube';
  }

  static clone(node: InlineYoutubeNode) {
    return new InlineYoutubeNode(node.__embedSrc, node.__width, node.__height, node.__key);
  }

  static importJSON(json: SerializedInlineYoutubeNode) {
    return $createInlineYoutubeNode(
      json.embedSrc,
      json.width,
      json.height,
    )
  }

  decorate() {
    return (
      <Suspense fallback={null}>
        <InlineComponent
          nodeKey={this.__key}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${this.__embedSrc}`}
            width={this.__width}
            height={this.__height}
            title="YouTube Video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen={true}
          />
        </InlineComponent>
      </Suspense>
    );
  }
}

export function $createInlineYoutubeNode(
  embedSrc: string,
  width?: number,
  height?: number,
  key?: string,
) {
  return $applyNodeReplacement(new InlineYoutubeNode(embedSrc, width, height, key));
}

export function isInlineYoutubeNode(node: unknown): node is InlineYoutubeNode {
  return node instanceof InlineYoutubeNode;
}